import spinnerImg from "../../assets/images/spinner.svg";

interface SpinnerProps {
  size?: "sm" | "md" | "lg" | "xl";
  overlay?: boolean;
  message?: string;
}

/**
 * Spinner Component - مكون التحميل المحسن
 *
 * يستخدم spinner.svg مع animation loop مستمر وتصميم جميل
 *
 * الاستخدامات:
 * 1. <Spinner /> - للصفحات الكاملة (الافتراضي)
 * 2. <Spinner size="sm" message="جاري الحفظ..." /> - حجم صغير مع رسالة مخصصة
 * 3. <SpinnerOverlay message="جاري المعالجة..." /> - overlay فوق المحتوى
 * 4. <SpinnerInline size="md" /> - للاستخدام داخل المكونات
 */
const Spinner = ({
  size = "lg",
  overlay = false,
  message = "جاري التحميل...",
}: SpinnerProps) => {
  // تحديد أحجام مختلفة للـ spinner
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-20 h-20 md:w-24 md:h-24",
    xl: "w-28 h-28 md:w-32 md:h-32",
  };

  const textSizeClasses = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-xl md:text-2xl",
    xl: "text-2xl md:text-3xl",
  };

  const spinnerContent = (
    <div className='text-center py-8'>
      {/* الصورة مع الأنيميشن */}
      <div className='flex justify-center mb-6'>
        <img
          src={spinnerImg}
          alt='جاري التحميل'
          className={`${sizeClasses[size]} animate-spin`}
          style={{
            filter: "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.2))",
            animationDuration: "2s",
            animationTimingFunction: "linear",
            animationIterationCount: "infinite",
          }}
        />
      </div>

      {/* النص */}
      <div className='space-y-2'>
        <p
          className={`${textSizeClasses[size]} font-bold text-red-700 animate-pulse`}
        >
          {message}
        </p>
        <div className='flex justify-center space-x-1 rtl:space-x-reverse'>
          <div className='w-2 h-2 bg-red-600 rounded-full animate-bounce'></div>
          <div
            className='w-2 h-2 bg-red-600 rounded-full animate-bounce'
            style={{ animationDelay: "0.1s" }}
          ></div>
          <div
            className='w-2 h-2 bg-red-600 rounded-full animate-bounce'
            style={{ animationDelay: "0.2s" }}
          ></div>
        </div>
      </div>
    </div>
  );

  if (overlay) {
    return (
      <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
        <div className='bg-white rounded-lg p-8 shadow-xl'>
          {spinnerContent}
        </div>
      </div>
    );
  }

  return (
    <div className='flex items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-white'>
      {spinnerContent}
    </div>
  );
};

/**
 * SpinnerOverlay - مكون overlay للعمليات السريعة
 *
 * الاستخدام:
 * const [isProcessing, setIsProcessing] = useState(false);
 *
 * {isProcessing && <SpinnerOverlay message="جاري حفظ البيانات..." />}
 */
export const SpinnerOverlay = ({
  message = "جاري المعالجة...",
}: {
  message?: string;
}) => <Spinner size='md' overlay message={message} />;

/**
 * SpinnerInline - مكون صغير للاستخدام داخل المكونات
 *
 * الاستخدام:
 * {isLoading && <SpinnerInline message="جاري التحقق..." />}
 */
export const SpinnerInline = ({
  size = "sm",
  message,
}: {
  size?: "sm" | "md";
  message?: string;
}) => (
  <div className='flex items-center justify-center py-4'>
    <div className='text-center'>
      <img
        src={spinnerImg}
        alt='جاري التحميل'
        className={`${
          size === "sm" ? "w-6 h-6" : "w-10 h-10"
        } animate-spin mx-auto mb-2`}
        style={{
          filter: "drop-shadow(0 2px 4px rgba(220, 38, 38, 0.2))",
          animationDuration: "1.5s",
          animationTimingFunction: "linear",
          animationIterationCount: "infinite",
        }}
      />
      {message && (
        <p
          className={`${
            size === "sm" ? "text-sm" : "text-base"
          } text-red-700 font-medium`}
        >
          {message}
        </p>
      )}
    </div>
  </div>
);

export default Spinner;
